import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, addMonths, addYears, endOfMonth, endOfYear, format, startOfMonth, startOfYear } from 'date-fns';
import { AlertTriangle, CalendarCheck, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usuarioProfileService, type DiasTrabalhadosResposta } from '@/services/api';
import {
  SINAL, SINAIS_FILTRO, SinalFiltro, LIMITE_DIAS_SEM_REGISTRO, ehDiaUtil, inicioDaSemana, resumirDiasTrabalhados,
  somarDias,
} from '@/lib/diasTrabalhados';
import { AjudaSinais } from '@/components/usuarios/AjudaSinais';
import { useAuth } from '@/contexts/AuthContext';

const formatarDia = (ymd: string | null | undefined) =>
  ymd ? new Date(`${ymd}T12:00:00`).toLocaleDateString('pt-BR') : 'nenhum';

/** Sinais mostrados dia a dia (mesmas cores em chips, bolinhas e legenda). */
const SINAIS_DIA = [
  { bit: SINAL.acesso, label: 'App', cor: '#3b82f6' },
  { bit: SINAL.agenda, label: 'Agenda', cor: '#ec4899' },
  { bit: SINAL.acao, label: 'Ação', cor: '#f59e0b' },
];

type EstadoDia = 'ok' | 'sem' | 'folga' | 'fora';

const COR_ESTADO: Record<EstadoDia, string> = {
  ok: 'bg-emerald-500 text-white',
  sem: 'bg-rose-200 text-rose-900 dark:bg-rose-950 dark:text-rose-200',
  folga: 'bg-muted text-muted-foreground',
  fora: 'border border-dashed border-border text-muted-foreground',
};

/** Faixa de aviso no topo da página do usuário quando ele está sinalizado (decisão 10c do plano). */
export function AvisoSemRegistro({ resposta, userId }: { resposta: DiasTrabalhadosResposta | null; userId: string }) {
  if (!resposta) return null;
  const r = resumirDiasTrabalhados(resposta.usuarios[userId], resposta.hoje, {}, 'todos');
  if (!r.sinalizado) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        <strong>{r.diasSemRegistro} dias úteis seguidos sem registro</strong> (limite: {LIMITE_DIAS_SEM_REGISTRO}).
        Último registro (acesso, ação no app ou evento na agenda): {formatarDia(r.ultimoRegistro)}.
      </span>
    </div>
  );
}

type Escala = 'semana' | 'mes' | 'ano';

const ESCALAS: { key: Escala; label: string }[] = [
  { key: 'semana', label: 'Semana' },
  { key: 'mes', label: 'Mês' },
  { key: 'ano', label: 'Ano' },
];

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Janela visível (YYYY-MM-DD) da escala a partir de uma data de referência. */
function janelaDe(escala: Escala, ref: Date): { de: string; ate: string; titulo: string } {
  if (escala === 'semana') {
    const de = inicioDaSemana(format(ref, 'yyyy-MM-dd'));
    const ate = somarDias(de, 6);
    return { de, ate, titulo: `${formatarDia(de).slice(0, 5)} a ${formatarDia(ate)}` };
  }
  if (escala === 'mes') {
    return {
      de: format(startOfMonth(ref), 'yyyy-MM-dd'),
      ate: format(endOfMonth(ref), 'yyyy-MM-dd'),
      titulo: `${MESES[ref.getMonth()]}/${ref.getFullYear()}`,
    };
  }
  return {
    de: format(startOfYear(ref), 'yyyy-MM-dd'),
    ate: format(endOfYear(ref), 'yyyy-MM-dd'),
    titulo: String(ref.getFullYear()),
  };
}

function mover(escala: Escala, ref: Date, passo: number): Date {
  if (escala === 'semana') return addDays(ref, 7 * passo);
  if (escala === 'mes') return addMonths(ref, passo);
  return addYears(ref, passo);
}

/**
 * Card "Dias trabalhados" da Visão geral: escala Semana/Mês/Ano com navegação ‹ › (como no
 * Calendário), independente do período da página. Grade horizontal: dias da semana nas linhas,
 * semanas nas colunas.
 */
export function DiasTrabalhadosCard({ userId, dataInicial }: { userId: string; dataInicial?: string }) {
  const navigate = useNavigate();
  const { user, setEmpresaFiltroId } = useAuth();
  const [sinal, setSinal] = useState<SinalFiltro>('todos');
  const [escala, setEscala] = useState<Escala>('mes');
  const [ref, setRef] = useState<Date>(() => (dataInicial ? new Date(`${dataInicial}T12:00:00`) : new Date()));
  const [resposta, setResposta] = useState<DiasTrabalhadosResposta | null>(null);
  const [carregando, setCarregando] = useState(false);

  const janela = useMemo(() => janelaDe(escala, ref), [escala, ref]);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    usuarioProfileService.getDiasTrabalhados(userId, { de: janela.de, ate: janela.ate }).then((r) => {
      if (cancelado) return;
      setResposta(r);
      setCarregando(false);
    });
    return () => { cancelado = true; };
  }, [userId, janela.de, janela.ate]);

  const usuario = resposta?.usuarios[userId];
  const hoje = resposta?.hoje ?? format(new Date(), 'yyyy-MM-dd');
  // A contagem começa no que vier depois: início da janela ou início da pessoa no sistema (cadastro ou
  // primeiro registro) — antes disso os dias não são "sem sinal", a pessoa ainda não existia.
  const inicioPessoa = useMemo(() => {
    const primeiroDia = Object.keys(usuario?.dias ?? {}).sort()[0];
    return [usuario?.criado_em, primeiroDia].filter(Boolean).sort()[0] as string | undefined;
  }, [usuario]);
  const deEfetivo = inicioPessoa && inicioPessoa > janela.de ? inicioPessoa : janela.de;
  const resumo = useMemo(
    () => resumirDiasTrabalhados(usuario, hoje, { de: deEfetivo, ate: janela.ate }, sinal),
    [usuario, hoje, deEfetivo, janela.ate, sinal],
  );

  // Colunas = semanas (segunda a domingo) que cobrem a janela; cada coluna tem 7 dias (usado no Ano).
  const semanas = useMemo(() => {
    const colunas: string[][] = [];
    for (let seg = inicioDaSemana(janela.de); seg <= janela.ate; seg = somarDias(seg, 7)) {
      colunas.push(Array.from({ length: 7 }, (_, i) => somarDias(seg, i)));
    }
    return colunas;
  }, [janela.de, janela.ate]);

  const diasDaJanela = useMemo(() => {
    const lista: string[] = [];
    for (let d = janela.de; d <= janela.ate; d = somarDias(d, 1)) lista.push(d);
    return lista;
  }, [janela.de, janela.ate]);

  const mascara = sinal === 'todos' ? SINAL.acesso | SINAL.agenda | SINAL.acao : SINAL[sinal];

  const estadoDo = (dia: string): EstadoDia => {
    if (dia > hoje || dia < deEfetivo) return 'fora';
    if ((usuario?.dias[dia] ?? 0) & mascara) return 'ok';
    return ehDiaUtil(dia) ? 'sem' : 'folga';
  };
  const sinaisDo = (dia: string) => SINAIS_DIA.filter((x) => (usuario?.dias[dia] ?? 0) & x.bit);
  const temEvento = (dia: string) => Boolean((usuario?.dias[dia] ?? 0) & SINAL.agenda);

  /** Dia com sinal de Agenda: busca o(s) evento(s) daquele dia e leva pro Calendário já aberto
   *  nele (decisão da Samantha, 28/09/2026) — se houver mais de um, só leva pro dia. Pra
   *  superadmin, o Calendário exige uma empresa selecionada no filtro do topo antes de mostrar
   *  qualquer coisa — ajusta esse filtro pra empresa do evento antes de navegar, senão a pessoa
   *  cai numa tela de "selecione uma empresa" sem saber qual é (achado 28/09/2026). */
  const abrirDiaNoCalendario = async (dia: string) => {
    const res = await usuarioProfileService.getAtividade(userId, 'calendario', { page: 1, limit: 5, de: dia, ate: dia });
    const eventos = (res.data ?? []) as { id?: string; empresa_id?: string | null }[];
    if (user?.tipo === 'superadmin' && eventos[0]?.empresa_id) {
      setEmpresaFiltroId(eventos[0].empresa_id);
    }
    if (eventos.length === 1 && eventos[0].id) {
      navigate(`/calendario?data=${dia}&evento=${eventos[0].id}`);
    } else {
      navigate(`/calendario?data=${dia}`);
    }
  };
  const dicaDo = (dia: string) => {
    const e = estadoDo(dia);
    const quando = `${formatarDia(dia)}${ehDiaUtil(dia) ? '' : ' (fim de semana/feriado)'}`;
    if (dia > hoje) return `${quando}: ainda não chegou`;
    if (dia < deEfetivo) return `${quando}: antes do início da pessoa no sistema`;
    const nomes = sinaisDo(dia).map((x) => x.label);
    return `${quando}: ${nomes.length ? nomes.join(', ') : e === 'sem' ? 'sem registro' : 'sem sinal'}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <CalendarCheck className="h-4 w-4" /> Dias trabalhados
          </CardTitle>
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">Contar por <AjudaSinais /></span>
            <div className="flex gap-1">
              {SINAIS_FILTRO.map((s) => (
                <Button
                  key={s.key}
                  variant={sinal === s.key ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  title={s.descricao}
                  onClick={() => setSinal(s.key)}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <div className="flex gap-1">
              {ESCALAS.map((e) => (
                <Button key={e.key} variant={escala === e.key ? 'default' : 'outline'} size="sm" className="h-7 px-2 text-xs" onClick={() => setEscala(e.key)}>
                  {e.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setRef((r) => mover(escala, r, -1))} title="Anterior">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[110px] text-center text-sm font-medium">{janela.titulo}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setRef((r) => mover(escala, r, 1))} title="Próximo">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setRef(new Date())}>Hoje</Button>
            </div>
            {carregando && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-baseline gap-1.5 rounded-md bg-muted px-3 py-1.5">
              <strong className="text-xl leading-none">{resumo.diasTrabalhados}</strong>
              <span className="text-xs text-muted-foreground">dias trabalhados</span>
            </div>
            <div className="flex items-baseline gap-1.5 rounded-md bg-muted px-3 py-1.5">
              <strong className="text-xl leading-none">{resumo.diasUteisSemSinal}</strong>
              <span className="text-xs text-muted-foreground">de {resumo.diasUteis} dias úteis sem sinal</span>
            </div>
            <span className="text-xs text-muted-foreground">
              App {resumo.porSinal.acesso} · Agenda {resumo.porSinal.agenda} · Ação {resumo.porSinal.acao}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className={`h-3 w-3 rounded-sm ${COR_ESTADO.ok}`} /> com sinal</span>
            <span className="flex items-center gap-1"><span className={`h-3 w-3 rounded-sm ${COR_ESTADO.sem}`} /> dia útil sem sinal</span>
            <span className="flex items-center gap-1"><span className={`h-3 w-3 rounded-sm ${COR_ESTADO.folga}`} /> fim de semana/feriado</span>
            <span className="flex items-center gap-1"><span className={`h-3 w-3 rounded-sm ${COR_ESTADO.fora}`} /> futuro / antes do início</span>
          </div>
        </div>

        {escala === 'semana' && (
          // Semana: um cartão por dia, com os sinais escritos
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {diasDaJanela.map((dia, i) => {
              const e = estadoDo(dia);
              const clicavel = e !== 'fora' && temEvento(dia);
              return (
                <div
                  key={dia}
                  className={`flex min-h-[112px] flex-col gap-1.5 rounded-lg p-2.5 ${COR_ESTADO[e]} ${clicavel ? 'cursor-pointer ring-offset-1 hover:ring-2 hover:ring-foreground/40' : ''}`}
                  title={dicaDo(dia)}
                  onClick={clicavel ? () => abrirDiaNoCalendario(dia) : undefined}
                >
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span>{DIAS_SEMANA[i]}</span>
                    <span className="tabular-nums">{formatarDia(dia).slice(0, 5)}</span>
                  </div>
                  {e !== 'fora' && sinaisDo(dia).map((x) => (
                    <span key={x.bit} className="inline-flex w-fit items-center gap-1 rounded-full bg-white/85 px-2 py-0.5 text-[11px] font-semibold text-neutral-800">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: x.cor }} />{x.label}
                    </span>
                  ))}
                  {e === 'sem' && <span className="text-[11px] font-semibold">sem registro</span>}
                  {e === 'fora' && <span className="text-[11px]">{dia > hoje ? 'ainda não chegou' : 'antes do início'}</span>}
                </div>
              );
            })}
          </div>
        )}

        {escala === 'mes' && (
          // Mês: grade de calendário, número do dia e uma bolinha por sinal
          <div className="grid grid-cols-7 gap-1.5">
            {DIAS_SEMANA.map((d) => (
              <span key={d} className="text-center text-[11px] font-semibold text-muted-foreground">{d}</span>
            ))}
            {Array.from({ length: (new Date(`${janela.de}T12:00:00Z`).getUTCDay() + 6) % 7 }, (_, i) => <span key={`vazio-${i}`} />)}
            {diasDaJanela.map((dia) => {
              const e = estadoDo(dia);
              const clicavel = e !== 'fora' && temEvento(dia);
              return (
                <div
                  key={dia}
                  className={`flex min-h-[56px] flex-col justify-between rounded-md p-1.5 ${COR_ESTADO[e]} ${clicavel ? 'cursor-pointer ring-offset-1 hover:ring-2 hover:ring-foreground/40' : ''}`}
                  title={dicaDo(dia)}
                  onClick={clicavel ? () => abrirDiaNoCalendario(dia) : undefined}
                >
                  <span className="text-xs font-bold tabular-nums">{Number(dia.slice(8))}</span>
                  {e !== 'fora' && (
                    <span className="flex justify-center gap-1">
                      {sinaisDo(dia).map((x) => <span key={x.bit} className="h-1.5 w-1.5 rounded-full" style={{ background: x.cor }} />)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {escala === 'ano' && (
          // Ano: mapa de calor na largura toda — semanas nas colunas, divisória nos meses, fim de semana mais baixo
          <div className="overflow-x-auto pb-1">
            <div
              className="grid min-w-[480px] gap-[3px]"
              style={{ gridTemplateColumns: `30px repeat(${semanas.length}, minmax(7px, 1fr))` }}
            >
              <span />
              {semanas.map((coluna, i) => {
                const primeiroDoMes = coluna.find((d) => d.endsWith('-01') && d >= janela.de && d <= janela.ate);
                return (
                  <span
                    key={coluna[0]}
                    className={`whitespace-nowrap text-[10px] leading-4 text-muted-foreground ${primeiroDoMes && i > 0 ? 'border-l-2 border-border pl-0.5' : ''}`}
                  >
                    {primeiroDoMes ? MESES[Number(primeiroDoMes.slice(5, 7)) - 1] : ''}
                  </span>
                );
              })}
              {DIAS_SEMANA.map((nome, linha) => (
                <Fragment key={nome}>
                  <span className="flex items-center text-[10px] text-muted-foreground">{nome}</span>
                  {semanas.map((coluna) => {
                    const dia = coluna[linha];
                    if (dia < janela.de || dia > janela.ate) return <span key={dia} />;
                    const e = estadoDo(dia);
                    const clicavel = e !== 'fora' && temEvento(dia);
                    return (
                      <span
                        key={dia}
                        className={`aspect-square rounded-[3px] ${COR_ESTADO[e]} ${clicavel ? 'cursor-pointer ring-offset-1 hover:ring-2 hover:ring-foreground/40' : ''}`}
                        title={dicaDo(dia)}
                        onClick={clicavel ? () => abrirDiaNoCalendario(dia) : undefined}
                      />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        )}

        {escala !== 'ano' && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            {SINAIS_DIA.map((x) => (
              <span key={x.bit} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: x.cor }} /> {x.label}</span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
